'use client';

// T14 — MediaDetailPanel: the always-visible detail column for the selected
// media slot (preview + metadata + AI damage list + upload/replace/delete
// actions). Replaces the legacy media tab's inline lightbox damage panel,
// but as a standalone column rather than a modal — the
// full-screen zoom itself is a separate component (MediaZoomModal, T16);
// this component only exposes `onZoom` to trigger it.

import * as React from 'react';
import { Badge, Button } from '@/components/ui';
import type { DamageItem, DamageResult, EnrichmentKind, MediaRecord } from './types';
import type { EnrichmentState, KindState } from './hooks/useMediaAiEnrichment';
import { applyWatermarkToFile, normalizeImageFile, type GPS } from './watermark';

const OCR_KIND_LABELS: Partial<Record<EnrichmentKind, string>> = {
  plate_ocr: 'Registration plate',
  vin_ocr: 'VIN / chassis plate',
};

export interface MediaDetailPanelProps {
  selected: MediaRecord | null;
  slotLabel: string;
  enrichment?: EnrichmentState;
  onZoom?: () => void;
  onDelete?: () => void;
  onReplace?: (file: File) => void;
  onUpload?: (file: File) => void;
  onRetryEnrichment?: (kind: EnrichmentKind) => void;
  mode?: 'staff' | 'portal';
  // Only needed for the empty state (no `selected` yet to derive photo vs
  // video from) — restricts the camera-capture input's accept type so
  // mobile browsers launch the correct capture UI (photo vs video) instead
  // of falling back to a generic file picker.
  slotKind?: 'photo' | 'video';
  // Slot code of the currently-selected slot (e.g. 'front_view', 'selfie').
  // Used only to pick the camera facing hint for portal "Take Photo"/"Retake":
  // the 'selfie' slot opens the front camera (capture="user"), every other
  // slot the rear camera (capture="environment"). It is a hint the browser
  // may honour or ignore — the user can still flip cameras either way.
  slotCode?: string;
  // Current GPS fix (if any) for stamping camera-captured photos with a
  // timestamp + lat/long watermark. Only applied to photos taken via the
  // "Take Photo"/"Retake" (camera) inputs — never to gallery uploads (a
  // gallery photo's real capture time/location may not match "now") and
  // never to videos (out of scope).
  gps?: GPS | null;
}

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatUploadedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

function DamageItemCard({ item }: { item: DamageItem }) {
  return (
    <div className="space-y-1.5 rounded-md border border-neutral-200 bg-neutral-50 p-2.5 dark:border-neutral-700 dark:bg-neutral-800">
      <div className="flex flex-wrap items-center justify-between gap-1.5">
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{item.part_name}</p>
        {item.estimated_cost?.recommended_cost != null && (
          <Badge variant={item.estimated_cost.estimate_basis === 'replacement' ? 'error' : 'success'} size="sm">
            ₹{item.estimated_cost.recommended_cost.toLocaleString('en-IN')}
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400">
        <span>{item.damage_type}</span>
        <span aria-hidden="true">·</span>
        <span>{item.repair_or_replace === 'replace' ? 'Replace' : 'Repair'}</span>
      </div>
      {item.is_pre_existing && (
        // Hedged language deliberately, per DEC-11's risk mitigation — this is
        // a heuristic signal for the adjuster to review, not a certain finding.
        <div className="rounded-sm border border-warning-200 bg-warning-50 px-2 py-1 text-xs text-warning-900 dark:border-warning-800 dark:bg-warning-900/30 dark:text-warning-100">
          <p className="font-medium">Possibly pre-existing damage</p>
          {item.pre_existing_reasoning && <p className="mt-0.5">{item.pre_existing_reasoning}</p>}
        </div>
      )}
    </div>
  );
}

function DamageSection({
  damage,
  detecting,
  error,
  onRetry,
}: {
  damage: DamageResult | null | undefined;
  detecting: boolean;
  error?: string;
  onRetry?: () => void;
}) {
  return (
    <div data-testid="media-damage-section" className="space-y-2">
      <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">AI Damage Analysis</h3>

      {detecting && (
        <div className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-2 py-1.5 dark:border-primary-800 dark:bg-primary-900/30">
          <div className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-b-2 border-primary-500" />
          <span className="text-sm text-primary-700 dark:text-primary-300">Detecting damage…</span>
        </div>
      )}

      {!detecting && error && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-error-200 bg-error-50 px-2 py-1.5 text-sm text-error-700 dark:border-error-800 dark:bg-error-900/30 dark:text-error-200">
          <span>{error}</span>
          {onRetry && (
            <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      )}

      {!detecting && !error && !damage && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-neutral-500">Not yet analyzed.</p>
          {onRetry && (
            <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
              Analyze
            </Button>
          )}
        </div>
      )}

      {!detecting && !error && damage && damage.damages.length === 0 && (
        <p className="text-sm text-success-600">No damage detected.</p>
      )}

      {!detecting && !error && damage && damage.damages.length > 0 && (
        <div className="space-y-2">
          {damage.damages.map((item, index) => (
            <DamageItemCard key={index} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

export function MediaDetailPanel({
  selected,
  slotLabel,
  enrichment,
  onZoom,
  onDelete,
  onReplace,
  onUpload,
  onRetryEnrichment,
  mode = 'staff',
  slotKind,
  slotCode,
  gps,
}: MediaDetailPanelProps) {
  const uploadInputRef = React.useRef<HTMLInputElement>(null);
  const captureInputRef = React.useRef<HTMLInputElement>(null);
  const replaceInputRef = React.useRef<HTMLInputElement>(null);
  const retakeInputRef = React.useRef<HTMLInputElement>(null);
  const isPortal = mode === 'portal';
  // Same restriction as portal share-link viewers - garage/claim office
  // users never see the AI Damage Analysis section (cost/severity
  // breakdown + Analyze button).
  const isGarage =
    typeof window !== 'undefined' &&
    ['GARAGE', 'CLAIM_OFFICE'].includes(localStorage.getItem('user_type')?.toUpperCase() ?? '');
  // The selfie slot wants the front ("user") camera; every other slot the
  // rear ("environment") camera. Hint only — the user can still switch.
  const cameraFacing = slotCode === 'selfie' ? 'user' : 'environment';

  async function handleCameraFile(file: File, isPhotoSlot: boolean, deliver: (f: File) => void) {
    if (!isPhotoSlot) { deliver(file); return; }
    try {
      deliver(await applyWatermarkToFile(file, gps ?? null));
    } catch {
      // Fail open — never block an upload just because watermarking failed.
      deliver(file);
    }
  }

  // Gallery / "Upload" / "Replace" pick: re-encode to a clean image/jpeg so a
  // phone's wrong or missing content-type (image/jpg, application/octet-stream,
  // iPhone HEIC) never reaches the API — that used to hard-400 the upload.
  // Videos and non-photo slots pass straight through.
  async function handleGalleryFile(file: File, isPhotoSlot: boolean, deliver: (f: File) => void) {
    if (!isPhotoSlot) { deliver(file); return; }
    deliver(await normalizeImageFile(file));
  }

  if (!selected) {
    const emptyStateAccept = slotKind === 'video' ? 'video/*' : 'image/*';
    const emptyStateIsPhoto = slotKind !== 'video';
    return (
      <div
        data-testid="media-detail-panel"
        className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-neutral-300 p-8 text-center dark:border-neutral-700"
      >
        <p className="text-sm text-neutral-500">No file uploaded yet for {slotLabel}.</p>
        {onUpload && (
          <div className="flex items-center gap-2">
            {isPortal && (
              <>
                <Button type="button" variant="primary" size="sm" onClick={() => captureInputRef.current?.click()}>
                  Take Photo
                </Button>
                <input
                  ref={captureInputRef}
                  type="file"
                  className="hidden"
                  accept={emptyStateAccept}
                  capture={cameraFacing}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) handleCameraFile(file, emptyStateIsPhoto, onUpload);
                  }}
                />
              </>
            )}
            <Button type="button" variant={isPortal ? 'secondary' : 'primary'} size="sm" onClick={() => uploadInputRef.current?.click()}>
              Upload
            </Button>
            <input
              ref={uploadInputRef}
              type="file"
              className="hidden"
              accept={emptyStateAccept}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) handleGalleryFile(file, emptyStateIsPhoto, onUpload);
              }}
            />
          </div>
        )}
      </div>
    );
  }

  const isPhoto = !!selected.photo_type;
  const damage = enrichment?.results.damage ?? selected.detected_damage ?? null;
  // Read the damage_detection KIND's own status, not the aggregate — a
  // sibling kind (plate_ocr/vin_ocr) failing on the same photo must not hide
  // a successful damage result or mislabel its error as a damage failure.
  const damageKindState = enrichment?.byKind?.damage_detection;
  const detecting = damageKindState?.status === 'running';
  const error = damageKindState?.status === 'error' ? damageKindState.error : undefined;

  const ocrErrors = isPhoto
    ? (Object.entries(enrichment?.byKind ?? {}) as [EnrichmentKind, KindState][]).filter(
        ([kind, state]) => kind !== 'damage_detection' && state.status === 'error',
      )
    : [];

  return (
    <div data-testid="media-detail-panel" className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{slotLabel}</h2>

      {isPhoto ? (
        <button
          type="button"
          onClick={onZoom}
          aria-label={`Zoom ${slotLabel}`}
          className="block overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={selected.download_url} alt={selected.file_name ?? slotLabel} className="w-full object-cover" />
        </button>
      ) : (
        <div className="relative overflow-hidden rounded-lg border border-neutral-200 bg-black dark:border-neutral-700">
          <video
            key={selected.id}
            src={selected.download_url}
            controls
            className="max-h-80 w-full"
          />
          {onZoom && (
            <button
              type="button"
              onClick={onZoom}
              aria-label={`Zoom ${slotLabel}`}
              className="absolute right-2 top-2 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-black/80"
            >
              Zoom
            </button>
          )}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
        <dt className="text-neutral-500">File name</dt>
        <dd className="text-neutral-900 dark:text-neutral-100">{selected.file_name ?? '—'}</dd>
        <dt className="text-neutral-500">Size</dt>
        <dd className="text-neutral-900 dark:text-neutral-100">{formatFileSize(selected.file_size)}</dd>
        <dt className="text-neutral-500">Uploaded</dt>
        <dd className="text-neutral-900 dark:text-neutral-100">{formatUploadedAt(selected.uploaded_at)}</dd>
        {selected.extracted_reg_number && (
          <>
            <dt className="text-neutral-500">Reg. number</dt>
            <dd className="text-neutral-900 dark:text-neutral-100">{selected.extracted_reg_number}</dd>
          </>
        )}
        {selected.extracted_vin_number && (
          <>
            <dt className="text-neutral-500">VIN</dt>
            <dd className="text-neutral-900 dark:text-neutral-100">{selected.extracted_vin_number}</dd>
          </>
        )}
      </dl>

      {ocrErrors.length > 0 && (
        <div className="space-y-1.5">
          {ocrErrors.map(([kind, state]) => (
            <div
              key={kind}
              data-testid={`media-ocr-error-${kind}`}
              className="flex items-center justify-between gap-2 rounded-md border border-error-200 bg-error-50 px-2 py-1.5 text-sm text-error-700 dark:border-error-800 dark:bg-error-900/30 dark:text-error-200"
            >
              <span>
                {OCR_KIND_LABELS[kind] ?? kind}: {state.error}
              </span>
              {onRetryEnrichment && (
                <Button type="button" variant="ghost" size="sm" onClick={() => onRetryEnrichment(kind)}>
                  Retry
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {isPhoto && mode !== 'portal' && !isGarage && (
        <DamageSection
          damage={damage}
          detecting={detecting}
          error={error}
          onRetry={onRetryEnrichment ? () => onRetryEnrichment('damage_detection') : undefined}
        />
      )}

      <div className="flex items-center gap-2 pt-2">
        {onReplace && (
          <>
            {isPortal && (
              <>
                <Button type="button" variant="secondary" size="sm" onClick={() => retakeInputRef.current?.click()}>
                  Retake
                </Button>
                <input
                  ref={retakeInputRef}
                  type="file"
                  className="hidden"
                  accept={isPhoto ? 'image/*' : 'video/*'}
                  capture={cameraFacing}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) handleCameraFile(file, isPhoto, onReplace);
                  }}
                />
              </>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={() => replaceInputRef.current?.click()}>
              Replace
            </Button>
            <input
              ref={replaceInputRef}
              type="file"
              className="hidden"
              accept={isPhoto ? 'image/*' : 'video/*'}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) handleGalleryFile(file, isPhoto, onReplace);
              }}
            />
          </>
        )}
        {onDelete && (
          <Button type="button" variant="danger" size="sm" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}
